<template>
    <hts-drawer
        v-model="visible"
        color="main"
        :submit="handleSubmitFormAddRemooc"
        size="large"
        :rules="rules"
        :title="$t('labels.remoocModule.add')"
        :undo-enable="true"
        @close="closeOverSide"
        @undo="undo()">
        <hts-card :title="$t('labels.generalInformation')">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <hts-select-remooc-category
                    v-model="dataFormAddRemooc.remoocType"
                    v-model:name="dataFormAddRemooc.nameTypeRemooc"
                    required
                    prop="remoocType"
                    @change="handleInputVehicleLoad" />
                <hts-form-input
                    v-model="dataFormAddRemooc.licensePlate"
                    :label="$t('labels.remoocModule.licensePlate')"
                    :color="props.color"
                    required
                    name="licensePlate"
                    prop="licensePlate" />
                <hts-form-input
                    v-model="dataFormAddRemooc.manufacture"
                    :label="$t('labels.remoocModule.manufacture')"
                    :color="props.color"
                    required
                    name="manufacture"
                    prop="manufacture" />
                <hts-form-input
                    v-model="dataFormAddRemooc.load"
                    :label="$t('labels.vehicleLoadByKg')"
                    :color="props.color"
                    type="number"
                    required
                    name="vehicleLoadByKg"
                    prop="load" />
                <hts-form-input
                    v-model="dataFormAddRemooc.weight"
                    :label="$t('labels.weightRemoocKG')"
                    :color="props.color"
                    type="number"
                    required
                    name="weight"
                    prop="weight" />
                <hts-form-date
                    v-model="dataFormAddRemooc.buyingTime"
                    :label="$t('labels.vehicleModule.buyingTime')"
                    :color="props.color"
                    :required="true"
                    type="year"
                    name="buyingTime"
                    prop="buyingTime" />
                <hts-form-input
                    v-model="dataFormAddRemooc.price"
                    :label="$t('labels.remoocModule.price')"
                    :color="props.color"
                    type="number"
                    required
                    name="price"
                    prop="price" />
                <!--
                  <hts-form-select
                  v-model="dataFormAddRemooc.coveringContainerLevel"
                  :label="$t('labels.vehicleModule.coveringContainerLevel')"
                  required
                  :items="coveringContainerLevel"
                  key-id="value"
                  key-label="label"
                  :color="props.color"
                  name="coveringContainerLevel"
                  prop="coveringContainerLevel" />
                -->
                <hts-form-input
                    v-model="dataFormAddRemooc.description"
                    :label="$t('labels.description')"
                    :color="props.color"
                    name="description"
                    prop="description" />
            </div>
        </hts-card>
        <hts-card
            v-if="checkPermission(layoutPermissions.remooc_edit_pricing)"
            :title="$t('labels.remoocManager')">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <hts-form-input
                    v-model="dataFormAddRemooc.depreciation"
                    :label="$t('labels.depreciationPeriodRemoocByYear')"
                    type="number"
                    :color="props.color"
                    required
                    name="depreciationPeriodRemoocByYear"
                    prop="depreciation" />
                <hts-form-input
                    v-model="dataFormAddRemooc.sellingPrice"
                    :label="$t('labels.remoocModule.sellingPrice')"
                    type="number"
                    :color="props.color"
                    required
                    name="sellingPrice"
                    prop="sellingPrice" />
            </div>
        </hts-card>
        <hts-card :title="$t('labels.truckInformation')">
            <hts-form-checkbox
                v-model="dataFormAddRemooc.isTowingContainer"
                class="mt-5"
                :items="[{ label: $t('labels.qualificationDragContainer'), value: false }]"
                @change="handleCheckbox" />

            <hts-form-select
                v-if="dataFormAddRemooc.isTowingContainer"
                v-model="dataFormAddRemooc.towingCapacity"
                :label="$t('labels.suitableContainerType')"
                :required="true"
                :items="towingCapacityList"
                key-id="value"
                key-label="label"
                :color="props.color"
                multiple
                name="suitableContainerType"
                prop="containerType" />
        </hts-card>
    </hts-drawer>
</template>

<script setup lang="ts">
import { CoveringContainerLevel, GoodsTypes } from '../imports/form'
import form, { IRemooc } from '../imports/remooc'
import { $api, $lodash } from '#imports'
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  }
})

const store = $store()
const dataFormAddRemooc = reactive<IRemooc>({ ...form.initRemooc })
const emits = defineEmits(['close', 'refreshTables', 'newVehicleCategory'])
const goodsTypesList = ref<Array<String>>([])
const visible = ref(false)
const openAddRemooc = () => {
  visible.value = true
  dataFormAddRemooc.goodsType = [GoodsTypes.LIQUID, GoodsTypes.SOLID, GoodsTypes.GAS]
}
defineExpose({
  openAddRemooc
})
const closeOverSide = () => {
  visible.value = false
}

const undo = () => {
  Object.assign(dataFormAddRemooc, { ...form.initRemooc })
  goodsTypesList.value = []
}

const handleInputVehicleLoad = $lodash.debounce((value: Array<{ [key: string]: any }>) => {
  if (value.length) {
    dataFormAddRemooc.depreciation = value?.[0]?.depreciationPeriodRemooc || null
    dataFormAddRemooc.sellingPrice = value?.[0]?.priceLiquidation || null
  }
}, 300)

const goodsTypes = [
  {
    value: GoodsTypes.LIQUID,
    label: $t('labels.liquid')
  },
  {
    value: GoodsTypes.SOLID,
    label: $t('labels.solid')
  },
  {
    value: GoodsTypes.GAS,
    label: $t('labels.gas')
  }
]
const towingCapacityList = [
  {
    value: '20',
    label: $t('labels.vehicleModule.20ft')
  },
  {
    value: '40',
    label: $t('labels.vehicleModule.40ft')
  },
  {
    value: '45',
    label: $t('labels.vehicleModule.45ft')
  }
]
const handleCheckbox = (val: any) => {
  if (!val) {
    dataFormAddRemooc.towingCapacity = []
  }
}
const handleSubmitFormAddRemooc = async () => {
  const api = $url.vehicle.remooc.add
  const data: any = await $api(api, { body: { ...dataFormAddRemooc } })
  const resData: any = data?.data?.value
  if (resData?.success) {
    Object.assign(dataFormAddRemooc, { ...form.initRemooc })
    emits('refreshTables')
    process.client && $toast().success($t('labels.addSuccess'))
    emits('close')
    undo()
    closeOverSide()
  }
}

const yearNow = new Date().getFullYear()
const rules = computed(() => {
  const data: { [key: string]: any } = {}
  data.buyingTime = 'min_value:1971|max_value:' + yearNow
  data.depreciation = 'min_value:1|integer'

  if (typeof dataFormAddRemooc.sellingPrice === 'number' && typeof dataFormAddRemooc.price === 'number') {
    if (checkPermission(layoutPermissions.remooc_edit_pricing)) {
      data.sellingPrice = `required|validationCustom:${dataFormAddRemooc.sellingPrice < dataFormAddRemooc.price}--Giá thanh lý phải nhỏ hơn giá mua`
    }
    data.price = `required|validationCustom:${dataFormAddRemooc.sellingPrice < dataFormAddRemooc.price}--Giá mua phải lớn hơn giá thanh lý`
  }

  return data
})

watch(
  () => dataFormAddRemooc.nameTypeRemooc,
  (val) => {
    if (val === $t('labels.vehicleModule.20ft')) {
      dataFormAddRemooc.towingCapacity = ['20']
    }
    if (val === $t('labels.vehicleModule.40ft')) {
      dataFormAddRemooc.towingCapacity = ['20', '40']
    }
    if (val === $t('labels.vehicleModule.45ft')) {
      dataFormAddRemooc.towingCapacity = ['20', '40', '45']
    }
  }
)

const coveringContainerLevel = [
  {
    value: CoveringContainerLevel.PRIVATE,
    label: $t('labels.vehicleModule.private')
  },
  {
    value: CoveringContainerLevel.OPEN,
    label: $t('labels.vehicleModule.open')
  }
]
</script>
